import {createReducer, on, Action} from '@ngrx/store';
import * as DigitalBoardActions from './digital-board.actions';
import Order from 'src/app/models/Order';
import {OrderPool} from 'src/app/models/OrderPool';

export const DIGITAL_BOARD_FEATURE_KEY = 'digitalBoard';

export interface DigitalBoardState {
	loaded: boolean;
	loading: boolean;
	error: string | null;
	inPreparationOrders: (Order | OrderPool)[];
	readyOrders: (Order | OrderPool)[];
	venueId: string | null;
}

export const initialState: DigitalBoardState = {
	loaded: false,
	loading: false,
	error: null,
	inPreparationOrders: [],
	readyOrders: [],
	venueId: null
};

const digitalBoardReducer = createReducer(
	initialState,

	on(DigitalBoardActions.loadDigitalBoardData, (state, {venueId}) => ({
		...state,
		loading: true,
		error: null,
		venueId
	})),

	on(
		DigitalBoardActions.loadDigitalBoardDataSuccess,
		(state, {inPreparationOrders, readyOrders}) => ({
			...state,
			loading: false,
			loaded: true,
			error: null,
			inPreparationOrders,
			readyOrders
		})
	),

	on(DigitalBoardActions.loadDigitalBoardDataFailure, (state, {error}) => ({
		...state,
		loading: false,
		loaded: false,
		error
	})),

	on(DigitalBoardActions.updateBoardOrder, (state, {order}) => {
		const isInPreparation =
			order.status === 'in_preparation' || order.status === 'ready';
		const isReady = order.status === 'ready';

		// Update in preparation orders
		const updatedInPreparation = [...state.inPreparationOrders];
		const existingInPrepIndex = updatedInPreparation.findIndex(
			item => item._id === order._id
		);

		if (existingInPrepIndex !== -1) {
			// Order exists, update or remove based on new status
			if (isInPreparation) {
				updatedInPreparation[existingInPrepIndex] = order;
			} else {
				updatedInPreparation.splice(existingInPrepIndex, 1);
			}
		} else if (isInPreparation) {
			// New order, add it
			updatedInPreparation.push(order);
		}

		// Update ready orders
		const updatedReady = [...state.readyOrders];
		const existingReadyIndex = updatedReady.findIndex(
			item => item._id === order._id
		);

		if (existingReadyIndex !== -1) {
			// Order exists, update or remove based on new status
			if (isReady) {
				updatedReady[existingReadyIndex] = order;
			} else {
				updatedReady.splice(existingReadyIndex, 1);
			}
		} else if (isReady) {
			// New order, add it
			updatedReady.push(order);
		}

		return {
			...state,
			inPreparationOrders: updatedInPreparation,
			readyOrders: updatedReady
		};
	}),

	on(DigitalBoardActions.updateBoardOrderPool, (state, {orderPool}) => {
		const isInPreparation =
			orderPool.orderStatus === 'in_preparation' ||
			orderPool.orderStatus === 'ready';
		const isReady = orderPool.orderStatus === 'ready';

		// Update in preparation orders
		const updatedInPreparation = [...state.inPreparationOrders];
		const existingInPrepIndex = updatedInPreparation.findIndex(
			item => item._id === orderPool._id
		);

		if (existingInPrepIndex !== -1) {
			// Order pool exists, update or remove based on new status
			if (isInPreparation) {
				updatedInPreparation[existingInPrepIndex] = orderPool;
			} else {
				updatedInPreparation.splice(existingInPrepIndex, 1);
			}
		} else if (isInPreparation) {
			// New order pool, add it
			updatedInPreparation.push(orderPool);
		}

		// Update ready orders
		const updatedReady = [...state.readyOrders];
		const existingReadyIndex = updatedReady.findIndex(
			item => item._id === orderPool._id
		);

		if (existingReadyIndex !== -1) {
			// Order pool exists, update or remove based on new status
			if (isReady) {
				updatedReady[existingReadyIndex] = orderPool;
			} else {
				updatedReady.splice(existingReadyIndex, 1);
			}
		} else if (isReady) {
			// New order pool, add it
			updatedReady.push(orderPool);
		}

		return {
			...state,
			inPreparationOrders: updatedInPreparation,
			readyOrders: updatedReady
		};
	})
);

export function reducer(state: DigitalBoardState | undefined, action: Action) {
	return digitalBoardReducer(state, action);
}
